/**
 * Gateway HTTP client — the single funnel every dashboard adapter and
 * overlay screen uses to reach wiki-gateway's `/api/v1` (ARCHITECTURE
 * §3). Session-cookie auth (httpOnly, credentials: "include"), CSRF
 * header on mutations, `{ok,…}` envelope unwrapping, 401 fan-out to the
 * auth provider.
 */
import { ApiRoutes, CSRF_HEADER } from "@llm-wiki/shared"
import type { LoginResponse, MeResponse } from "@llm-wiki/shared"

/** Same-origin by default — the gateway serves the dashboard assets. */
export const GATEWAY_BASE: string =
  (import.meta.env.VITE_GATEWAY_URL as string | undefined)?.replace(/\/+$/, "") ?? ""

export class GatewayError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message)
    this.name = "GatewayError"
  }
}

let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

export function getCsrfToken(): string | null {
  return csrfToken
}

type UnauthorizedListener = () => void
const unauthorizedListeners = new Set<UnauthorizedListener>()

/** AuthProvider subscribes to flip back to the login screen on 401. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

function notifyUnauthorized(): void {
  for (const l of unauthorizedListeners) {
    try {
      l()
    } catch {
      // listener errors must not break the failing request's own path
    }
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  /** JSON body (mutations). */
  body?: unknown
  /** Multipart body — wins over `body` when set. */
  formData?: FormData
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
  headers?: Record<string, string>
}

export function buildUrl(
  path: string,
  query?: RequestOptions["query"],
): string {
  const url = new URL(GATEWAY_BASE + path, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v))
    }
  }
  return url.toString()
}

/**
 * Raw fetch with auth semantics; returns the Response. Used directly
 * for binary reads (media) and SSE-over-POST (chat).
 */
export async function gatewayFetch(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  const method = options.method ?? "GET"
  const headers: Record<string, string> = { ...options.headers }
  let body: BodyInit | undefined
  if (options.formData) {
    body = options.formData
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json"
    body = JSON.stringify(options.body)
  }
  if (method !== "GET" && csrfToken) {
    headers[CSRF_HEADER] = csrfToken
  }

  const res = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    body,
    credentials: "include",
    signal: options.signal,
  })
  if (res.status === 401) notifyUnauthorized()
  return res
}

/**
 * JSON request against the `{ok: true, …} | {ok: false, error}` envelope.
 * Resolves with the parsed body (ok === true asserted); throws
 * GatewayError otherwise.
 */
export async function api<T extends { ok: true } = { ok: true }>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const res = await gatewayFetch(path, options)
  let parsed: unknown = null
  try {
    parsed = await res.json()
  } catch {
    // non-JSON error body (proxy 502 etc.) — fall through
  }
  const envelope = parsed as { ok?: boolean; error?: string; code?: string } | null
  if (!res.ok || !envelope || envelope.ok !== true) {
    throw new GatewayError(
      envelope?.error ?? `${res.status} ${res.statusText}`,
      res.status,
      envelope?.code,
    )
  }
  return envelope as T
}

// ── Auth convenience wrappers (login screen / AuthProvider) ─────────────

export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const result = await api<LoginResponse>(ApiRoutes.login(), {
    method: "POST",
    body: { email, password },
  })
  setCsrfToken(result.csrfToken)
  return result
}

export async function logout(): Promise<void> {
  try {
    await api(ApiRoutes.logout(), { method: "POST" })
  } finally {
    setCsrfToken(null)
  }
}

export async function fetchMe(): Promise<MeResponse | null> {
  const res = await gatewayFetch(ApiRoutes.me())
  if (res.status === 401) return null
  const parsed = (await res.json()) as MeResponse & { csrfToken?: string }
  if (!res.ok || parsed.ok !== true) return null
  // Session restore: the gateway echoes a fresh CSRF token on /auth/me.
  if (typeof parsed.csrfToken === "string") setCsrfToken(parsed.csrfToken)
  return parsed
}
